import React, { useMemo } from "react";
import DataTable from "react-data-table-component";

const SelectAllHeader = ({
  saveSelection,
  tableData,
  pageSelectionSet,
  onSelectAllToggle,
}) => {
  const isGlobalAll = saveSelection === "selected All";
  const allOnPageSelected =
    tableData.length > 0 &&
    tableData.every((row) => pageSelectionSet.has(String(row.id)));
  const checked = isGlobalAll || allOnPageSelected;
  const indeterminate =
    !isGlobalAll && pageSelectionSet.size > 0 && !allOnPageSelected;

  return (
    <input
      type="checkbox"
      className="qr-row-checkbox"
      aria-label="Select all students"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      onChange={(e) => {
        e.stopPropagation();
        onSelectAllToggle();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const RowCheckbox = ({ row, pageSelectionSet, onRowToggle }) => {
  const isSelected = pageSelectionSet.has(String(row.id));
  return (
    <input
      type="checkbox"
      className="qr-row-checkbox"
      aria-label="Select row"
      checked={isSelected}
      onChange={(e) => {
        e.stopPropagation();
        onRowToggle(row, e.target.checked);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const GpaCell = ({ value }) => {
  const val = parseFloat(value);
  const bg = isNaN(val)
    ? "#f9fafb"
    : val >= 3.5
    ? "#f0fdf4"
    : val >= 3.0
    ? "#fffbeb"
    : val >= 2.5
    ? "#fef2f2"
    : "#f9fafb";
  const color = isNaN(val)
    ? "#6b7280"
    : val >= 3.5
    ? "#059669"
    : val >= 3.0
    ? "#d97706"
    : val >= 2.5
    ? "#dc2626"
    : "#6b7280";
  return (
    <div
      style={{
        backgroundColor: bg,
        color,
        padding: "6px 10px",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {isNaN(val) ? "N/A" : val.toFixed(2)}
    </div>
  );
};

const tableCustomStyles = {
  table: { style: { width: "max-content", minWidth: "100%" } },
  tableWrapper: {
    style: {
      display: "block",
      width: "max-content",
      minWidth: "100%",
      overflow: "visible",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderBottom: "2px solid #e2e8f0",
      minHeight: "44px",
      width: "max-content",
      minWidth: "100%",
    },
  },
  headCells: {
    style: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#374151",
      textTransform: "none",
      letterSpacing: "normal",
      padding: "8px 12px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      borderBottom: "2px solid #e2e8f0",
    },
  },
  rows: {
    style: {
      minHeight: "56px",
      fontSize: "13px",
      borderBottom: "none",
      width: "max-content",
      minWidth: "100%",
    },
  },
  cells: {
    style: {
      padding: "8px 12px",
      flexShrink: 0,
      borderBottom: "1px solid #f1f5f9",
      backgroundColor: "inherit",
    },
  },
  pagination: { style: { display: "none" } },
};

const MyStudentTable = ({
  tableData,
  saveSelection,
  pageSelectionSet,
  onRowToggle,
  onSelectAllToggle,
  sortField,
  sortDirection,
  onSort,
  selectedCount,
}) => {
  const columns = useMemo(
    () => [
      {
        name: (
          <SelectAllHeader
            saveSelection={saveSelection}
            tableData={tableData}
            pageSelectionSet={pageSelectionSet}
            onSelectAllToggle={onSelectAllToggle}
          />
        ),
        width: "52px",
        minWidth: "52px",
        sortable: false,
        cell: (row) => (
          <RowCheckbox
            row={row}
            pageSelectionSet={pageSelectionSet}
            onRowToggle={onRowToggle}
          />
        ),
      },
      {
        name: "Email",
        selector: (row) => row["0"] || "",
        sortField: "0",
        id: "0",
        sortable: true,
        minWidth: "260px",
        grow: 2,
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#eff6ff",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "12px",
              color: "#1e40af",
              fontWeight: 500,
            }}
          >
            {row["0"] || "No email"}
          </span>
        ),
      },
      {
        name: "First name",
        selector: (row) => row["1"] || "",
        sortField: "1",
        id: "1",
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#059669", fontSize: "14px" }}>
            {row["1"] || "-"}
          </span>
        ),
      },
      {
        name: "Last name",
        selector: (row) => row["2"] || "",
        sortField: "2",
        id: "2",
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#059669", fontSize: "14px" }}>
            {row["2"] || "-"}
          </span>
        ),
      },
      {
        name: "Ethnicity",
        selector: (row) => row["3"] || "",
        sortField: "3",
        id: "3",
        sortable: true,
        minWidth: "120px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#eef2ff",
              color: "#4338ca",
              padding: "4px 8px",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {row["3"] || "N/A"}
          </span>
        ),
      },
      {
        name: "Gender",
        selector: (row) => row["4"] || "",
        sortField: "4",
        id: "4",
        sortable: true,
        minWidth: "110px",
        cell: (row) => (
          <div
            style={{
              backgroundColor:
                row["4"] === "Male"
                  ? "#fef3f2"
                  : row["4"] === "Female"
                  ? "#f0f9ff"
                  : "#f9fafb",
              color:
                row["4"] === "Male"
                  ? "#dc2626"
                  : row["4"] === "Female"
                  ? "#2563eb"
                  : "#6b7280",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {row["4"] || "Not specified"}
          </div>
        ),
      },
      {
        name: "Overall GPA",
        selector: (row) => row["5"] || "",
        sortField: "5",
        id: "5",
        sortable: true,
        minWidth: "120px",
        cell: (row) => <GpaCell value={row["5"]} />,
      },
      {
        name: "Grade",
        selector: (row) => row["6"] || "",
        sortField: "6",
        id: "6",
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f9fafb",
              color: "#374151",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              border: "1px solid #e5e7eb",
              fontWeight: 600,
            }}
          >
            {row["6"] || "-"}
          </span>
        ),
      },
      {
        name: "School Name",
        selector: (row) => row["7"] || "",
        sortField: "7",
        id: "7",
        sortable: true,
        minWidth: "280px",
        grow: 2,
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f9fafb",
              padding: "4px 8px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              fontSize: "13px",
              color: "#374151",
              fontWeight: 500,
            }}
          >
            {row["7"] || "No school specified"}
          </span>
        ),
      },
    ],
    [tableData, saveSelection, pageSelectionSet, onRowToggle, onSelectAllToggle]
  );

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => pageSelectionSet.has(String(row.id)),
        style: { backgroundColor: "#e0f2fe" },
      },
    ],
    [pageSelectionSet]
  );

  return (
    <div className="qr-rdt-wrapper">
      {selectedCount > 0 && (
        <div className="qr-selection-bar">
          {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
        </div>
      )}
      <div className="qr-table-scroll">
        <DataTable
          className="result-table"
          columns={columns}
          data={tableData}
          keyField="id"
          sortServer
          onSort={onSort}
          {...(sortField
            ? {
                defaultSortFieldId: sortField,
                defaultSortAsc: sortDirection === "asc",
              }
            : {})}
          pagination={false}
          customStyles={tableCustomStyles}
          conditionalRowStyles={conditionalRowStyles}
          dense
          persistTableHead
          noDataComponent={
            <div style={{ padding: "24px", color: "#6b7280" }}>No students found</div>
          }
        />
      </div>
    </div>
  );
};

export default MyStudentTable;
